import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TextField, Button, Box, Typography, Paper, CircularProgress } from '@mui/material';
import { getApiUrl } from '../config';
import { trackAuthEvent, trackError } from '../utils/analytics';
import PageHead from '../components/PageHead';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setLoading(true);

    try {
      // Step 1: Login
      const loginResponse = await axios.post(`${getApiUrl()}/auth/login`, {
        email,
        password
      });
      
      if (!loginResponse.data.token) {
        setMessage('Invalid login response');
        trackError('auth_error', 'Invalid login response - no token received');
        return;
      }

      const token = loginResponse.data.token;
      
      // Step 2: Store auth info
      localStorage.setItem('token', token);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('isAdmin', loginResponse.data.is_admin);
      localStorage.setItem('setupCompleted', loginResponse.data.setup_completed ? 'true' : 'false');

      // Track successful login
      trackAuthEvent('login_success', 'email');

      // Step 3: Call onLogin with the data from login response
      onLogin({
        is_admin: loginResponse.data.is_admin,
        setup_completed: loginResponse.data.setup_completed,
        login_method: 'email'
      });

      // Step 4: Navigate based on login response data
      if (!loginResponse.data.setup_completed) {
        console.log('Redirecting to setup wizard - setup not completed');
        navigate('/setupwizard');
      } else if (loginResponse.data.is_admin) {
        console.log('Redirecting to admin - setup completed, user is admin');
        navigate('/admin');
      } else {
        console.log('Redirecting to dashboard - setup completed, user is not admin');
        navigate('/dashboard');
      }

    } catch (error) {
      console.error('Error during login:', error);
      const errorMessage = error.response?.data?.error || 'An error occurred during login';
      setMessage(errorMessage);
      trackError('auth_error', errorMessage);
      trackAuthEvent('login_failed', 'email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)', // Matching landing page gradient
        padding: '20px',
      }}
    >
      <PageHead 
        title="Login" 
        description="Log in to your AutoBlogPilot account"
      />
      <Paper 
        elevation={3} 
        sx={{ 
          padding: 4, 
          width: '100%',
          maxWidth: 400,
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }}
      >
        <Typography 
          variant="h4" 
          align="center" 
          gutterBottom
          sx={{ 
            fontWeight: 800,
            background: 'linear-gradient(120deg, #2575fc 0%, #6a11cb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Welcome Back
        </Typography>

        {message && (
          <Typography 
            color="error" 
            align="center" 
            sx={{ mb: 2 }}
          >
            {message}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Password"
            variant="outlined"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.5,
              mb: 2,
              backgroundColor: '#2575fc',
              '&:hover': {
                backgroundColor: '#1c5cc9',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              onClick={() => navigate('/register')}
              sx={{ textTransform: 'none' }}
            >
              Create Account
            </Button>
            <Button
              onClick={() => navigate('/forgot-password')}
              sx={{ textTransform: 'none' }}
            >
              Forgot Password?
            </Button>
          </Box>
        </form>

        <Typography 
          align="center" 
          sx={{ 
            mt: 3,
            '& a': {
              color: '#2575fc',
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }}
        >
          Don't have an account? <a href="/register">Register here</a>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
